import { useEffect, useMemo, useState } from "react"
import { useRouter } from "next/router"
import { Show, SignIn, useAuth } from "@clerk/nextjs"
import { Box } from "@mui/material"
import { AuthPageContainer } from "auth/components/AuthPageContainer"
import { DevPasswordlessOverlay } from "auth/components/DevPasswordlessOverlay"
import { SlowRedirect } from "core/components/SlowRedirect"
import { ForgotPasswordLink } from "src/auth/containers/ForgotPasswordLink"
import { getClerkHashParams } from "src/auth/utils/get-clerk-hash-params"
import { getSigninReturnTo } from "src/auth/utils/get-signin-return-to"
import { SignUpLink } from "./SignUpLink"
import { AuthPageTerms } from "auth/components/AuthPageTerms"

const isSatelliteDomain =
  typeof window !== "undefined" && window.location.hostname.endsWith(".seam.vc")

export const SignInContainer = () => {
  const { query } = useRouter()
  const { isLoaded, isSignedIn } = useAuth()

  // Forward the satellite return URL (our return_to param, or Clerk's
  // redirect_url params from buildSignInUrl on e.g. the Ring account-link
  // page) through the signin-callback redirect. Depend on the two params
  // rather than the query object, whose identity changes on every router
  // transition.
  //
  // clerk-js buildSignInUrl() appends its params AFTER the hash
  // (`/signin#/?redirect_url=...`) because our <SignIn> is hash-routed, so
  // router.query never sees them — capture them from location.hash once on
  // mount (Clerk's hash router rewrites the hash as sign-in progresses, so
  // reading it later would lose them).
  //
  // A lazy useState initializer (not useEffect) is deliberate: React re-runs
  // the initializer during client hydration, so the first client render
  // already has the correct hashParams. An effect would render one frame with
  // {} first, letting Clerk's <SignIn> latch a bare /signin-callback
  // forceRedirectUrl. (<SignIn> renders nothing in the static build — it's
  // gated behind <Show when="signed-out"> until Clerk loads client-side — so
  // there's no server-HTML value to conflict with.)
  const [hashParams] = useState<Record<string, string>>(() =>
    typeof window === "undefined"
      ? {}
      : getClerkHashParams(window.location.hash)
  )
  // Forward the same hash to Clerk's sign-up link so a brand-new user
  // clicking "Sign up" from here doesn't lose the satellite return target
  // either — see SignUpLink.tsx for the equivalent standalone link.
  const [signUpUrl] = useState<string>(() =>
    typeof window === "undefined" ? "/signup" : `/signup${window.location.hash}`
  )
  const { return_to, redirect_url, sign_in_force_redirect_url } = query
  const callbackUrl = useMemo(() => {
    // Normalize router.query values first: duplicate query params arrive as
    // string[], which `??` would treat as present and let shadow the hash
    // fallback. getSigninReturnTo only accepts strings anyway.
    const queryReturnTo = typeof return_to === "string" ? return_to : undefined
    const queryRedirectUrl =
      typeof redirect_url === "string" ? redirect_url : undefined
    const querySignInForceRedirectUrl =
      typeof sign_in_force_redirect_url === "string"
        ? sign_in_force_redirect_url
        : undefined
    const returnTo = getSigninReturnTo({
      return_to: queryReturnTo ?? hashParams.return_to,
      sign_in_force_redirect_url:
        querySignInForceRedirectUrl ?? hashParams.sign_in_force_redirect_url,
      redirect_url: queryRedirectUrl ?? hashParams.redirect_url,
    })
    if (returnTo !== null) {
      return `/signin-callback?return_to=${encodeURIComponent(returnTo)}`
    }
    return "/signin-callback"
  }, [return_to, redirect_url, sign_in_force_redirect_url, hashParams])

  // Clerk's <SignIn> component only works on the primary domain. On satellite
  // domains, redirect signed-out users to the primary for authentication,
  // passing a return_to param so the user is sent back after auth.
  //
  // A seam session lives in per-domain localStorage, so the primary can only
  // mint one for itself — the satellite must run its own /signin-callback to
  // exchange the (synced) Clerk session for a seam session on this domain.
  // Bouncing an already-signed-in user to the primary therefore loops
  // forever: primary mints its own session, returns here, still no local
  // session, bounce again. Wait for Clerk to load and only bounce when the
  // user is definitively signed out.
  useEffect(() => {
    if (!isSatelliteDomain) return
    // In dev on local.seam.vc, skip the bounce entirely and let the local
    // /signin-callback do the exchange.
    // To use this map local server to local.seam.vc, add this to /etc/hosts:
    // sudo sh -c 'echo "127.0.0.1 local..seam.vc" >> /etc/hosts'
    // Also add https and launch server at 443 header. Ask Claude for help if needed.
    if (
      process.env.NODE_ENV === "development" &&
      window.location.hostname === "local.seam.vc"
    ) {
      window.location.href = "/signin-callback"
      return
    }
    if (!isLoaded) return
    if (isSignedIn) {
      window.location.href = callbackUrl
      return
    }
    const returnTo = encodeURIComponent(window.location.origin)
    window.location.href = `https://console.seam.co/signin?return_to=${returnTo}`
  }, [isLoaded, isSignedIn, callbackUrl])

  if (isSatelliteDomain) return null

  return (
    <Box width="100%" minHeight="100vh">
      <DevPasswordlessOverlay />
      <AuthPageContainer>
        <Show when="signed-out">
          <Box>
            <SignIn
              routing="hash"
              forceRedirectUrl={callbackUrl}
              signUpUrl={signUpUrl}
            />
            <AuthPageTerms />
            <SignUpLink />
            <ForgotPasswordLink />
          </Box>
        </Show>
        <Show when="signed-in">
          <SlowRedirect to={callbackUrl} />
        </Show>
      </AuthPageContainer>
    </Box>
  )
}
